import React, { useState, useCallback, useEffect, useRef } from 'react'
import { FiAlertCircle } from 'react-icons/fi'
import { IoClose } from 'react-icons/io5'

import { useField } from '@unform/core'

import Tooltip from '~/components/Tooltip'

import { Container } from '../Input/styles'

type SelectOption = {
  id?: string | number
  value: string | number
  text?: string
  label?: string
  disabled?: boolean
}

type SelectProps = Omit<
  React.SelectHTMLAttributes<HTMLSelectElement>,
  'onChange' | 'onBlur'
> & {
  name: string
  options?: SelectOption[] | null
  className?: string
  placeholder?: string
  showErrorMsg?: boolean
  onBlur?: (data: { name: string; value: string }) => void
  onChange?: (value: string) => void
}

/**
 *
 * @example
 *  <Select
 *    name="address_street"
 *    placeholder="Insira a rua"
 *    options={[
 *      { id: 1, value: 'option1', text: 'Option 1' },
 *      { id: 2, value: 'option2', text: 'Option 2' }
 *    ]}
 *  />
 *
 * @param {string} name Recebe o name para o select
 * @param {function} onBlur Extendendo onBlur
 * @param {Object[]} options Array com as options do select
 * @param {string|number} options[].id ID usado como key do map e no id da option
 * @param {string|number} options[].value Recebe o value da option
 * @param {string} options[].text Recebe o texto da option
 * @param {string} options[].label Recebe o label da option (alternativa ao text)
 * @param {boolean} options[].disabled Gerencia o estado da option
 *
 */

const Select: React.FC<SelectProps> = ({
  name,
  onBlur,
  onChange,
  options,
  className = '',
  placeholder = 'Escolha abaixo',
  showErrorMsg = false,
  ...rest
}) => {
  const selectRef = useRef<HTMLSelectElement>(null)

  const [isFilled, setIsFilled] = useState(false)

  const { fieldName, defaultValue, error, registerField, clearError } =
    useField(name)

  const handleInputBlur = useCallback(() => {
    setIsFilled(!!selectRef.current?.value)

    if (onBlur && selectRef.current) {
      onBlur({ name: selectRef.current.name, value: selectRef.current.value })
    }

    if (selectRef.current?.value) {
      clearError()
    }
  }, [clearError, onBlur])

  const handleChange = useCallback(() => {
    if (onChange && selectRef.current) {
      onChange(selectRef.current.value)
    }

    if (selectRef.current?.value) {
      clearError()
    }

    setIsFilled(!!selectRef.current?.value)
  }, [clearError, onChange])

  useEffect(() => {
    registerField({
      name: fieldName,
      ref: selectRef.current,
      path: 'value'
    })

    if (defaultValue) {
      setIsFilled(true)
    }
  }, [fieldName, registerField, defaultValue])

  return (
    <Container
      isErrored={!!error}
      style={{
        width: '100%'
      }}
    >
      {showErrorMsg && error && <div className="error">{error}</div>}
      <select
        name={fieldName}
        ref={selectRef}
        defaultValue={defaultValue || ''}
        className={`${className} ${error ? 'errorField' : ''}`}
        onBlur={handleInputBlur}
        onChange={handleChange}
        {...rest}
      >
        <option disabled value="">
          {placeholder}
        </option>
        {options &&
          options.map((option: SelectOption) => (
            <option
              key={option.value}
              value={option.value}
              id={option.id ? `${name}_${option.id}` : undefined}
              disabled={option.disabled || false}
            >
              {option.text || option.label}
            </option>
          ))}
      </select>

      {isFilled && (
        <button
          type="button"
          className="clear-button"
          style={{ padding: '0.5rem' }}
          onClick={() => {
            if (selectRef.current) {
              selectRef.current.value = ''
              handleChange()
              setIsFilled(false)
            }
          }}
        >
          <IoClose />
        </button>
      )}

      {error && <Tooltip text={error} icon={<FiAlertCircle />} />}
    </Container>
  )
}

export { Select }
